import type { CompositeQueryDefinition } from '@/types/chartDataSource';

export interface CompositeSourceScopeMetadata {
  schemaId: string;
  dataOwnerOrganizationId?: string;
  organizationId?: string;
  organizationName?: string;
}

export interface CompositeBindingSelection {
  definition: CompositeQueryDefinition;
  organizationId?: string;
  organizationName?: string;
}

export interface CompositeBindingResolution extends CompositeBindingSelection {
  error?: string;
}

export const PERSONAL_SPACE_COMPOSITE_SCOPE_ERROR =
  'This composite query uses sources without one shared organization scope. In personal space, choose sources from a single organization or switch into that organization before applying it.';
export const COMPOSITE_SOURCE_OWNER_ERROR =
  'Composite query sources must include explicit data owner organization IDs before saving or applying.';
export const COMPOSITE_SOURCE_OWNER_MISMATCH_ERROR =
  'Composite query source ownership changed during review. Re-select the source before saving or applying.';

function normalizeOptionalString(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : undefined;
}

function getMetadataDataOwnerOrganizationId(
  metadata: CompositeSourceScopeMetadata | undefined,
): string | undefined {
  return (
    normalizeOptionalString(metadata?.dataOwnerOrganizationId) ??
    normalizeOptionalString(metadata?.organizationId)
  );
}

function hasExplicitSourceOwnerMismatch(
  definition: CompositeQueryDefinition,
  sourceMetadata: CompositeSourceScopeMetadata[],
): boolean {
  const metadataBySchemaId = new Map(
    sourceMetadata.map((source) => [source.schemaId, source]),
  );

  return definition.sources.some((source) => {
    const metadataOwner = getMetadataDataOwnerOrganizationId(
      metadataBySchemaId.get(source.query.schemaId),
    );
    if (!metadataOwner) {
      return false;
    }

    const existingOwner = normalizeOptionalString(source.dataOwnerOrganizationId);
    const existingQueryOwner = normalizeOptionalString(source.query.organizationId);
    const hasSourceOwner = Boolean(existingOwner);
    const hasMismatchedSourceOwner =
      hasSourceOwner && existingOwner !== metadataOwner;
    const hasMismatchedQueryOwner =
      hasSourceOwner && Boolean(existingQueryOwner) && existingQueryOwner !== metadataOwner;

    return hasMismatchedSourceOwner || hasMismatchedQueryOwner;
  });
}

export function validateCompositeDefinitionSourceOwners(
  definition: CompositeQueryDefinition,
): string | null {
  if (
    definition.sources.length === 0 ||
    definition.sources.some(
      (source) => !normalizeOptionalString(source.dataOwnerOrganizationId),
    )
  ) {
    return COMPOSITE_SOURCE_OWNER_ERROR;
  }

  return null;
}

export function annotateCompositeQueryDefinitionOrganizations(
  definition: CompositeQueryDefinition,
  sourceMetadata: CompositeSourceScopeMetadata[],
): CompositeQueryDefinition {
  const metadataBySchemaId = new Map(
    sourceMetadata.map((source) => [source.schemaId, source]),
  );

  return {
    ...definition,
    sources: definition.sources.map((source) => {
      const metadata = metadataBySchemaId.get(source.query.schemaId);
      const dataOwnerOrganizationId =
        normalizeOptionalString(source.dataOwnerOrganizationId) ??
        getMetadataDataOwnerOrganizationId(metadata);
      const metadataOwnerOrganizationId = getMetadataDataOwnerOrganizationId(metadata);
      const metadataOrganizationName =
        dataOwnerOrganizationId &&
        (!metadataOwnerOrganizationId || dataOwnerOrganizationId === metadataOwnerOrganizationId)
          ? normalizeOptionalString(metadata?.organizationName)
          : undefined;
      const organizationName = dataOwnerOrganizationId
        ? metadataOrganizationName ?? normalizeOptionalString(source.query.organizationName)
        : undefined;
      const {
        organizationId: _sourceOrganizationId,
        organizationName: _sourceOrganizationName,
        ...queryWithoutOrganizationFields
      } = source.query;

      const sourceWithoutOwner = { ...source };
      delete sourceWithoutOwner.dataOwnerOrganizationId;

      return {
        ...sourceWithoutOwner,
        ...(dataOwnerOrganizationId ? { dataOwnerOrganizationId } : {}),
        query: {
          ...queryWithoutOrganizationFields,
          ...(dataOwnerOrganizationId
            ? { organizationId: dataOwnerOrganizationId }
            : {}),
          ...(organizationName ? { organizationName } : {}),
        },
      };
    }),
  };
}

export function getCompositeDefinitionOrganizationScope(
  definition: CompositeQueryDefinition,
): { organizationId: string; organizationName?: string } | null {
  if (definition.sources.length === 0) {
    return null;
  }

  const scopedSources = definition.sources.map((source) => ({
    organizationId: normalizeOptionalString(source.dataOwnerOrganizationId),
    organizationName: normalizeOptionalString(source.query.organizationName),
  }));

  if (scopedSources.some((source) => !source.organizationId)) {
    return null;
  }

  const firstOrganizationId = scopedSources[0].organizationId;
  if (!firstOrganizationId) {
    return null;
  }

  if (scopedSources.some((source) => source.organizationId !== firstOrganizationId)) {
    return null;
  }

  const organizationNames = scopedSources
    .map((source) => source.organizationName)
    .filter((name): name is string => Boolean(name));
  const firstOrganizationName = organizationNames[0];
  const organizationName = firstOrganizationName
    && organizationNames.every((name) => name === firstOrganizationName)
      ? firstOrganizationName
      : undefined;

  return organizationName
    ? { organizationId: firstOrganizationId, organizationName }
    : { organizationId: firstOrganizationId };
}

export function resolveCompositeBindingSelection(
  definition: CompositeQueryDefinition,
  sourceMetadata: CompositeSourceScopeMetadata[],
  options: { isPersonalSpace: boolean },
): CompositeBindingResolution {
  if (hasExplicitSourceOwnerMismatch(definition, sourceMetadata)) {
    return {
      definition,
      error: COMPOSITE_SOURCE_OWNER_MISMATCH_ERROR,
    };
  }

  const normalizedDefinition = annotateCompositeQueryDefinitionOrganizations(
    definition,
    sourceMetadata,
  );
  const ownerValidationError =
    validateCompositeDefinitionSourceOwners(normalizedDefinition);
  if (ownerValidationError) {
    return {
      definition: normalizedDefinition,
      error: ownerValidationError,
    };
  }

  if (!options.isPersonalSpace) {
    return {
      definition: normalizedDefinition,
    };
  }

  const scope = getCompositeDefinitionOrganizationScope(normalizedDefinition);
  if (!scope) {
    return {
      definition: normalizedDefinition,
      error: PERSONAL_SPACE_COMPOSITE_SCOPE_ERROR,
    };
  }

  return {
    definition: normalizedDefinition,
    organizationId: scope.organizationId,
    ...(scope.organizationName ? { organizationName: scope.organizationName } : {}),
  };
}
